<script setup>
/**
 * ItemDialogHeaderImage Component
 * Full-width image with placeholder and promo badge overlay
 * On mobile/tablet, tapping the image opens it fullscreen (lightbox)
 */
import { computed, ref } from 'vue'
import { useBackButtonClose } from '../../../../core/composables/useBackButtonClose.js'
import { getPromoBadgeLabel } from './itemDialogUtils.js'

const props = defineProps({
  image: {
    type: String,
    default: null,
  },
  itemName: {
    type: String,
    default: '',
  },
  badgeType: {
    type: String,
    default: null,
    validator: (v) => v === null || v === 'gold' || v === 'blue' || v === 'red' || v === 'green',
  },
  boxSizeText: {
    type: String,
    default: null,
  },
  expDate: {
    type: String,
    default: null,
  },
})

const hasImage = computed(() => !!props.image)
const badgeLabel = computed(() => getPromoBadgeLabel(props.badgeType))

// Fullscreen image viewer — mobile/tablet only (desktop already has a large pane)
const isLightboxOpen = ref(false)

function openLightbox() {
  if (!hasImage.value) return
  if (!window.matchMedia('(max-width: 1279px)').matches) return
  isLightboxOpen.value = true
}

function closeLightbox() {
  isLightboxOpen.value = false
}

// Hardware back closes the lightbox first, not the whole item dialog
useBackButtonClose(isLightboxOpen, closeLightbox)
</script>

<template>
  <div class="header-image-container" :class="{ zoomable: hasImage }" @click="openLightbox">
    <!-- Image -->
    <img v-if="hasImage" :src="image" :alt="itemName" class="header-image" loading="lazy" />

    <!-- Placeholder -->
    <div v-else class="image-placeholder">
      <div class="placeholder-content">
        <svg
          class="placeholder-icon"
          viewBox="0 0 24 24"
          width="64"
          height="64"
          fill="none"
          stroke="currentColor"
          stroke-width="1"
        >
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
          <circle cx="8.5" cy="8.5" r="1.5" />
          <polyline points="21 15 16 10 5 21" />
        </svg>
        <span class="placeholder-text">אין תמונה</span>
      </div>
      <!-- Decorative pattern -->
      <div class="placeholder-pattern"></div>
    </div>

    <!-- Box Size Overlay -->
    <div v-if="boxSizeText" class="box-size-overlay">
      <svg
        viewBox="0 0 24 24"
        width="14"
        height="14"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
      >
        <path
          d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"
        />
        <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
        <line x1="12" y1="22.08" x2="12" y2="12" />
      </svg>
      <span>{{ boxSizeText }}</span>
    </div>

    <!-- Exp Date Sticker (mobile only) -->
    <div v-if="expDate" class="exp-date-sticker">תוקף: {{ expDate }}</div>

    <!-- Promo Badge -->
    <div v-if="badgeType" class="promo-badge" :class="`promo-badge--${badgeType}`">
      <svg class="badge-icon" viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
        <polygon
          points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"
        />
      </svg>
      <span class="badge-text">{{ badgeLabel }}</span>
    </div>

    <!-- Zoom hint (mobile/tablet only) -->
    <div v-if="hasImage" class="zoom-hint" aria-hidden="true">
      <svg
        viewBox="0 0 24 24"
        width="14"
        height="14"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
      >
        <circle cx="11" cy="11" r="8" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
        <line x1="11" y1="8" x2="11" y2="14" />
        <line x1="8" y1="11" x2="14" y2="11" />
      </svg>
    </div>
  </div>

  <!-- Fullscreen Image Lightbox (mobile/tablet) -->
  <Teleport to="body">
    <Transition name="lightbox">
      <div
        v-if="isLightboxOpen"
        class="image-lightbox"
        role="dialog"
        aria-modal="true"
        :aria-label="itemName"
        @click="closeLightbox"
      >
        <button class="lightbox-close" aria-label="סגור" @click.stop="closeLightbox">
          <svg
            viewBox="0 0 24 24"
            width="22"
            height="22"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
        <img :src="image" :alt="itemName" class="lightbox-image" />
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.header-image-container {
  position: relative;
  width: 100%;
  height: 200px;
  flex-shrink: 0;
  background: #ffffff;
  overflow: hidden;
  transition: height 0.3s ease;
}

/* Image */
.header-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: #ffffff;
  transition: transform 0.3s ease;
}

/* Placeholder */
.image-placeholder {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, var(--bg-secondary) 0%, var(--bg-tertiary) 100%);
}

.placeholder-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-sm);
  z-index: 1;
}

.placeholder-icon {
  color: var(--text-muted);
  opacity: 0.4;
}

.placeholder-text {
  font-size: var(--font-size-sm);
  color: var(--text-muted);
  opacity: 0.6;
}

/* Decorative pattern overlay */
.placeholder-pattern {
  position: absolute;
  inset: 0;
  background-image:
    radial-gradient(circle at 20% 30%, rgba(30, 41, 59, 0.03) 0%, transparent 50%),
    radial-gradient(circle at 80% 70%, rgba(30, 41, 59, 0.03) 0%, transparent 50%);
  pointer-events: none;
}

/* Box Size Overlay */
.box-size-overlay {
  position: absolute;
  bottom: var(--spacing-sm);
  left: var(--spacing-sm);
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  padding: 4px 10px;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  color: white;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  border-radius: var(--border-radius-sm);
  white-space: nowrap;
}

.box-size-overlay svg {
  flex-shrink: 0;
  opacity: 0.85;
}

/* Promo Badge */
.promo-badge {
  position: absolute;
  top: var(--spacing-md);
  right: var(--spacing-md);
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  padding: 6px 12px;
  border-radius: var(--border-radius-sm);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  box-shadow: var(--shadow-md);
  animation: badgePop 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.promo-badge--gold {
  background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
  color: #78350f;
}

.promo-badge--red {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
}

.promo-badge--blue {
  background: var(--color-info);
  color: white;
}

.promo-badge--green {
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  color: white;
}

.badge-icon {
  flex-shrink: 0;
}

@keyframes badgePop {
  0% {
    opacity: 0;
    transform: scale(0.8);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}

/* Exp Date Sticker - mobile only */
.exp-date-sticker {
  display: none;
}

/* Zoom hint — visible on mobile/tablet where tap opens the lightbox */
.zoom-hint {
  display: none;
  position: absolute;
  bottom: var(--spacing-sm);
  right: var(--spacing-sm);
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(4px);
  color: white;
  border-radius: var(--border-radius-full);
  pointer-events: none;
}

@media (max-width: 1279px) {
  .header-image-container.zoomable {
    cursor: zoom-in;
  }

  .zoom-hint {
    display: flex;
  }
}

/* Fullscreen Image Lightbox */
.image-lightbox {
  position: fixed;
  inset: 0;
  z-index: 3500;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-lg);
  background: rgba(15, 23, 42, 0.92);
  cursor: zoom-out;
}

.lightbox-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.lightbox-close {
  position: absolute;
  top: var(--spacing-md);
  left: var(--spacing-md);
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  border: none;
  border-radius: var(--border-radius-full);
  color: white;
  cursor: pointer;
  transition: background var(--transition-fast);
}

.lightbox-close:hover {
  background: rgba(0, 0, 0, 0.7);
}

.lightbox-close:active {
  transform: scale(0.95);
}

.lightbox-enter-active,
.lightbox-leave-active {
  transition: opacity 0.25s ease;
}

.lightbox-enter-active .lightbox-image {
  transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.lightbox-enter-from,
.lightbox-leave-to {
  opacity: 0;
}

.lightbox-enter-from .lightbox-image {
  transform: scale(0.9);
}

/* Phone: compact thumbnail beside the title.
   Overlays (box size, exp date, promo badge) are hidden — the same info is
   shown as text in the dialog's title meta row and price box instead.
   Tablet (768+) keeps the full image pane with its overlays. */
@media (max-width: 767px) {
  .header-image-container {
    width: 116px;
    height: 116px;
    margin: var(--spacing-sm) var(--spacing-md);
    border: 1px solid var(--border-color);
    border-radius: var(--border-radius-sm);
  }

  .header-image {
    padding: 4px;
  }

  .box-size-overlay,
  .exp-date-sticker,
  .promo-badge {
    display: none;
  }

  .placeholder-icon {
    width: 36px;
    height: 36px;
  }

  .placeholder-text {
    font-size: 11px;
  }
}
</style>
